// renderInlineEditableElement extracted from OfferPageBuilderSimple.js lines 4739-9265
// Exported as useElementRenderer hook — call it to get the render function.
import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  FormControl,
  List,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../FAQAccordion';
import ComparisonTable from '../../ComparisonTable';
import TwoColumnComparisonTable from '../../TwoColumnComparisonTable';
import FittableImage from '../../FittableImage';
import CircularImageCropper from '../../CircularImageCropper';
import ImageCropper from '../../ImageCropper';
import Checklist from '../../Checklist';
import CenteredVideoPlayer from '../../CenteredVideoPlayer';
import ResizableButton from '../../ResizableButton';
import InlineRowLayout from '../../InlineRowLayout';
import ExtractedElementControls from '../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../textStyle.js';
import { getListIcon } from '../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../utils/offerPreviewElement';
import { renderTableElements } from './ElementRenderers/renderTableElements';
import { renderTextElements } from './ElementRenderers/renderTextElements';
import { renderImageElements } from './ElementRenderers/renderImageElements';
import { renderButtonElements } from './ElementRenderers/renderButtonElements';
import { renderVideoElements } from './ElementRenderers/renderVideoElements';
import { renderMiscElements } from './ElementRenderers/renderMiscElements';
import { renderListElements } from './ElementRenderers/renderListElements';
import { renderLayoutElements } from './ElementRenderers/renderLayoutElements';

export const useElementRenderer = ({
  editingElement,
  editingField,
  tempValue,
  setTempValue,
  textEditorRef,
  selectedElement,
  selectedElementContext,
  selectedSubElement,
  iconSelectorOpen,
  setIconSelectorOpen,
  isBrandsPage,
  paginationEnabled,
  currentPageIndex,
  pageSettings,
  forceRender,
  rightPanelOpen,
  setSelectedElement,
  setSelectedElementContext,
  setSelectedSubElement,
  setTabValue,
  setRightPanelOpen,
  setPages,
  setElements,
  setInsertAfterElementId,
  setPromptDialogTitle,
  setPromptDialogMessage,
  setPromptDialogValue,
  setPromptAction,
  setShowPromptDialog,
  startInlineEdit,
  saveInlineEdit,
  cancelInlineEdit,
  handleKeyPress,
  handleResizeStart,
  findAndUpdateLayout,
  moveElement,
  generateId,
  deleteElement,
  deleteElementFromZone,
  updateElement,
  applyHighlight,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  getZoneHeightStyle,
  getHeightModeText,
  toggleZoneHeight,
  getHeightTooltip,
  getExpandIcon,
  selectedVariantKey,
  setSelectedVariantKey,
  targetZone,
}) => {
  const renderInlineEditableElement = (element, isInZone = false, layoutId = null, zoneId = null, elementIndex = 0) => {
    const isEditing = editingElement?.elementId === element.id;
    const isEditingField = (field) => isEditing && editingField === field;

    const handleDoubleClick = (field, value) => {
      startInlineEdit(element.id, field, value, isInZone, layoutId, zoneId);
    };

    // Helper function to check if element is selected
    const isElementSelected = (elementId) => {
      return selectedElement?.id === elementId;
    };

    // Helper function to get selection styles
    const getSelectionStyles = (elementId) => {
      return isElementSelected(elementId) ? {
        outline: '3px solid #1976d2',
        outlineOffset: '2px',
        boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.2)'
      } : {};
    };

    // Element controls component — thin wrapper around the extracted
    // ElementControls so the 30 `<ElementControls />` call sites in the switch
    // below keep working without per-case prop threading.
    const ElementControls = () => (
      <ExtractedElementControls
        element={element}
        isInZone={isInZone}
        layoutId={layoutId}
        zoneId={zoneId}
        isBrandsPage={isBrandsPage}
        paginationEnabled={paginationEnabled}
        currentPageIndex={currentPageIndex}
        setInsertAfterElementId={setInsertAfterElementId}
        setTabValue={setTabValue}
        setRightPanelOpen={setRightPanelOpen}
        setPages={setPages}
        setElements={setElements}
        findAndUpdateLayout={findAndUpdateLayout}
        moveElement={moveElement}
        generateId={generateId}
        deleteElement={deleteElement}
        deleteElementFromZone={deleteElementFromZone}
      />
    );


    // Build context object for sub-renderers
    const ctx = {
      // Hook props (all 40+ parameters)
      editingElement, editingField, tempValue, setTempValue, textEditorRef,
      selectedElement, selectedElementContext, selectedSubElement,
      iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
      currentPageIndex, pageSettings, forceRender, rightPanelOpen,
      setSelectedElement, setSelectedElementContext, setSelectedSubElement,
      setTabValue, setRightPanelOpen, setPages, setElements,
      setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
      setPromptDialogValue, setPromptAction, setShowPromptDialog,
      startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
      handleResizeStart, findAndUpdateLayout, moveElement, generateId,
      deleteElement, deleteElementFromZone, updateElement, applyHighlight,
      handleDragOver, handleDragLeave, handleDrop,
      getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
      getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
      // Computed per-call helpers (close over element, isInZone, etc.)
      isEditing, isEditingField, handleDoubleClick,
      isElementSelected, getSelectionStyles, ElementControls,
      // Per-call args
      isInZone, layoutId, zoneId, elementIndex,
      // Self-reference for recursive LAYOUT rendering
      renderInlineEditableElement,
    };

    switch (element.type) {
      case ELEMENT_TYPES.FAQ_ACCORDION:
      case ELEMENT_TYPES.COMPARISON_TABLE:
      case ELEMENT_TYPES.TWO_COLUMN_COMPARISON:
      case ELEMENT_TYPES.STAR_RATING:
      case ELEMENT_TYPES.STAR_RATING_TABLE:
        return renderTableElements(element, ctx);

      case ELEMENT_TYPES.HEADING:
      case ELEMENT_TYPES.TEXT:
      case ELEMENT_TYPES.QUOTATION_BLOCK:
        return renderTextElements(element, ctx);

      case ELEMENT_TYPES.IMAGE:
      case ELEMENT_TYPES.CROPPABLE_IMAGE:
      case ELEMENT_TYPES.CIRCULAR_IMAGE:
      case ELEMENT_TYPES.FITTABLE_IMAGE:
      case ELEMENT_TYPES.IMAGE_OVERLAY:
        return renderImageElements(element, ctx);

      case ELEMENT_TYPES.BUTTON:
      case ELEMENT_TYPES.BUTTON_WITH_NOTICE:
      case ELEMENT_TYPES.BUTTON_IMAGE_CALLOUT:
      case ELEMENT_TYPES.BUTTON_TEXT_VARIANT:
      case ELEMENT_TYPES.BUTTON_HEADLINE_PRODUCT_IMAGE:
        return renderButtonElements(element, ctx);

      case ELEMENT_TYPES.VIDEO:
      case ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY:
      case ELEMENT_TYPES.MEDIA:
        return renderVideoElements(element, ctx);

      case ELEMENT_TYPES.HTML:
      case ELEMENT_TYPES.DIVIDER:
      case ELEMENT_TYPES.CHECKLIST:
        return renderMiscElements(element, ctx);

      case ELEMENT_TYPES.CUSTOM_LIST:
      case ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE:
      case ELEMENT_TYPES.TIMELINE_LIST:
      case ELEMENT_TYPES.PROCESS_STEPS:
        return renderListElements(element, ctx);

      case ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE:
      case ELEMENT_TYPES.LEFT_IMAGE_TEXT:
      case ELEMENT_TYPES.LAYOUT:
        return renderLayoutElements(element, ctx);

      default:
        return null;
    }
  };

  return renderInlineEditableElement;
};
